import type { ServerMessage } from 'ts-components/PiChat/protocol';
import { useSyncReducer } from 'ts-components/PiChat/versions/useSyncReducer';

export interface PushResult {
  action: 'created' | 'updated';
  dashboardId: string;
  dashletId: string;
  url: string;
}

interface BlockSessionState {
  pushResult: PushResult | null;
  dashletId: string | null;
  conversationId: string | null;
}

type BlockSessionAction =
  | { type: 'connecting' }
  | { type: 'server'; message: ServerMessage };

const INITIAL_BLOCK_SESSION: BlockSessionState = {
  pushResult: null,
  dashletId: null,
  conversationId: null,
};

const reduceServerMessage = (
  state: BlockSessionState,
  message: ServerMessage
): BlockSessionState => {
  switch (message.type) {
    case 'session.ready':
      return {
        ...state,
        conversationId: message.conversationId,
        dashletId: message.context.dashletId ?? state.dashletId,
      };
    case 'push.result':
      return {
        ...state,
        pushResult: {
          action: message.action,
          dashboardId: message.dashboardId,
          dashletId: message.dashletId,
          url: message.url,
        },
        conversationId: message.conversationId ?? state.conversationId,
        dashletId: message.dashletId,
      };
    case 'snapshot.restored':
      return message.push
        ? { ...state, pushResult: { ...message.push } }
        : state;
    default:
      return state;
  }
};

export const blockSessionReducer = (
  state: BlockSessionState,
  action: BlockSessionAction
): BlockSessionState => {
  switch (action.type) {
    case 'connecting':
      return INITIAL_BLOCK_SESSION;
    case 'server':
      return reduceServerMessage(state, action.message);
    default:
      return state;
  }
};

export const useBlockSession = () =>
  useSyncReducer(blockSessionReducer, INITIAL_BLOCK_SESSION);
